/**
 * AdminControlCentre — the one destination for RBAC + Hierarchy admin.
 *
 * Replaces (with redirects):
 *   /superadmin                    → tenants / plans / billing
 *   /admin/rbac-audit              → audit log
 *   /admin/role-builder            → permission matrix editor
 *   /admin/scopes                  → scope assignments
 *   /admin/team-permissions        → per-user capabilities
 *   /admin/permission-matrix       → role × permission grid
 *   /admin/churches/hierarchy      → HQ → Cell tree
 *
 * Layout:
 *   ┌───────────────┬────────────────────────────────────┐
 *   │ Live org tree │  Node detail (tabs)                │
 *   │  Platform     │   ▸ Overview                       │
 *   │  ├─ FWM HQ    │   ▸ People (with cascade indicator)│
 *   │  │  ├─ Cell 1 │   ▸ Roles & Ladder                 │
 *   │  ├─ School X  │   ▸ Vocabulary                     │
 *   └───────────────┴────────────────────────────────────┘
 *
 * All data comes from /api/admin/control-centre/* — the same tables
 * every legacy console reads. No separate data source.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { ViewAsLauncher } from '@/components/admin/ViewAs';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ShieldCheck, Building2, Church, GraduationCap, Users, Layers,
  ChevronRight, ChevronDown, Search, Loader2, Crown, User, X, Copy,
  CreditCard, LogIn, TrendingUp,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '@/components/ui/tabs';
import { authHeaders } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(),
      ...(opts.headers || {}),
    },
  });
  if (!res.ok) {
    const e = await res.json().catch(() => ({}));
    throw new Error(e.detail || `Request failed (${res.status})`);
  }
  return res.json();
};

const TIER_COLOR = {
  super_admin:    'bg-rose-100 text-rose-800 border-rose-300',
  platform_admin: 'bg-violet-100 text-violet-800 border-violet-300',
  org_admin:      'bg-indigo-100 text-indigo-800 border-indigo-300',
  manager:        'bg-emerald-100 text-emerald-800 border-emerald-300',
  operator:       'bg-sky-100 text-sky-800 border-sky-300',
  viewer:         'bg-slate-100 text-slate-700 border-slate-300',
};

const KIND_ICON = {
  church:      Church,
  school:      GraduationCap,
  event_body:  Layers,
  platform:    ShieldCheck,
  default:     Building2,
};


export default function AdminControlCentre() {
  const [selectedId, setSelectedId] = useState('platform:root');

  const { data: rolesData } = useQuery({
    queryKey: ['cc-roles'],
    queryFn: () => api('/api/admin/control-centre/roles'),
  });

  return (
    <div className="min-h-screen bg-slate-50" data-testid="control-centre">
      <Header actorRole={rolesData?.actor_role} tiers={rolesData?.tiers} />

      <div className="mx-auto max-w-[1400px] p-4 grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4">
        <TreePanel
          selectedId={selectedId}
          onSelect={setSelectedId}
          actorRole={rolesData?.actor_role}
        />
        <DetailPanel
          nodeId={selectedId}
          tiers={rolesData?.tiers || []}
          actorRole={rolesData?.actor_role}
        />
      </div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────────
// HEADER
// ────────────────────────────────────────────────────────────────

function Header({ actorRole, tiers }) {
  return (
    <div className="bg-white border-b border-slate-200">
      <div className="mx-auto max-w-[1400px] px-4 py-3 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-violet-600">Admin</div>
          <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-violet-600" /> Control Centre
          </h1>
        </div>
        {actorRole && (
          <div className="flex items-center gap-2" data-testid="cc-actor-role">
            <span className="text-[10px] uppercase tracking-widest text-slate-500">You are</span>
            <TierBadge tier={actorRole} />
          </div>
        )}
      </div>
      {tiers && (
        <div className="mx-auto max-w-[1400px] px-4 pb-3">
          <RoleLadder tiers={tiers} highlight={actorRole} />
        </div>
      )}
    </div>
  );
}


function RoleLadder({ tiers, highlight }) {
  return (
    <div className="flex flex-wrap items-center gap-1 text-[11px]" data-testid="cc-role-ladder">
      {tiers.map((t, i) => (
        <React.Fragment key={t.key}>
          <button
            type="button"
            title={t.blurb}
            data-testid={`cc-tier-${t.key}`}
            className={`px-2.5 py-1 rounded-full border transition ${
              highlight === t.key
                ? TIER_COLOR[t.key] + ' ring-2 ring-offset-1'
                : 'bg-white text-slate-600 border-slate-200'
            }`}
          >
            {t.label}
          </button>
          {i < tiers.length - 1 && (
            <ChevronRight className="h-3 w-3 text-slate-300 shrink-0" />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}


function TierBadge({ tier }) {
  return (
    <span
      data-testid={`cc-tier-badge-${tier}`}
      className={`text-[11px] px-2 py-0.5 rounded-full border font-medium ${TIER_COLOR[tier] || TIER_COLOR.viewer}`}
    >
      {tier?.replace('_', ' ')}
    </span>
  );
}


// ────────────────────────────────────────────────────────────────
// TREE PANEL (left rail)
// ────────────────────────────────────────────────────────────────

function TreePanel({ selectedId, onSelect, actorRole }) {
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState(() => new Set(['platform:root']));

  const { data, isLoading } = useQuery({
    queryKey: ['cc-tree'],
    queryFn: () => api('/api/admin/control-centre/tree'),
  });

  const nodes = data?.nodes || [];
  const children = useMemo(() => {
    const m = new Map();
    for (const n of nodes) {
      const arr = m.get(n.parent_id || null) || [];
      arr.push(n);
      m.set(n.parent_id || null, arr);
    }
    for (const arr of m.values()) arr.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    return m;
  }, [nodes]);

  const roots = actorRole && (actorRole === 'super_admin' || actorRole === 'platform_admin')
    ? (children.get(null) || [])
    : (children.get('platform:root') || []);

  const toggle = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  // If user searches, expand every node whose name matches
  const filteredIds = useMemo(() => {
    if (!query.trim()) return null;
    const needle = query.toLowerCase();
    const matches = nodes.filter((n) => (n.name || '').toLowerCase().includes(needle));
    const keep = new Set();
    for (const m of matches) {
      let cur = m;
      while (cur) {
        keep.add(cur.id);
        cur = nodes.find((n) => n.id === cur.parent_id);
      }
    }
    return keep;
  }, [nodes, query]);

  return (
    <aside className="rounded-lg border border-slate-200 bg-white overflow-hidden">
      <div className="p-3 border-b border-slate-200">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Organisation Tree</div>
        <div className="relative">
          <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-slate-400" />
          <Input
            data-testid="cc-tree-search"
            placeholder="Search organisations, sites…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-7 h-8 text-sm"
          />
        </div>
      </div>
      <div className="p-2 max-h-[70vh] overflow-y-auto" data-testid="cc-tree">
        {isLoading && <div className="p-4 text-center text-slate-400 text-sm"><Loader2 className="h-4 w-4 mx-auto animate-spin" /></div>}
        {roots.map((n) => (
          <TreeNode
            key={n.id}
            node={n}
            childrenMap={children}
            depth={0}
            selectedId={selectedId}
            onSelect={onSelect}
            expanded={expanded}
            onToggle={toggle}
            filteredIds={filteredIds}
          />
        ))}
      </div>
    </aside>
  );
}


function TreeNode({ node, childrenMap, depth, selectedId, onSelect, expanded, onToggle, filteredIds }) {
  const kids = childrenMap.get(node.id) || [];
  const hasKids = kids.length > 0;
  const isOpen = expanded.has(node.id) || filteredIds;
  const isSelected = node.id === selectedId;
  const Icon = KIND_ICON[node.kind || node.type] || KIND_ICON.default;

  if (filteredIds && !filteredIds.has(node.id)) return null;

  return (
    <div>
      <div
        className={`w-full flex items-center gap-1 rounded px-1.5 py-1 text-sm ${
          isSelected ? 'bg-violet-100 text-violet-900' : 'hover:bg-slate-100 text-slate-700'
        }`}
        style={{ paddingLeft: `${depth * 12 + 4}px` }}
      >
        {hasKids ? (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onToggle(node.id); }}
            className="text-slate-400 hover:text-slate-600 shrink-0"
            aria-label="Toggle"
          >
            {isOpen
              ? <ChevronDown className="h-3.5 w-3.5" />
              : <ChevronRight className="h-3.5 w-3.5" />}
          </button>
        ) : (
          <span className="w-3.5 shrink-0" />
        )}
        <button
          type="button"
          onClick={() => onSelect(node.id)}
          data-testid={`cc-node-${node.id}`}
          className="flex items-center gap-1 min-w-0 flex-1 text-left"
        >
          <Icon className="h-3.5 w-3.5 text-slate-500 shrink-0" />
          <span className="truncate flex-1">{node.name}</span>
          {node.type !== 'platform' && (
            <span className="text-[9px] uppercase text-slate-400 shrink-0">{node.type}</span>
          )}
        </button>
      </div>
      {isOpen && hasKids && (
        <div>
          {kids.map((k) => (
            <TreeNode
              key={k.id}
              node={k}
              childrenMap={childrenMap}
              depth={depth + 1}
              selectedId={selectedId}
              onSelect={onSelect}
              expanded={expanded}
              onToggle={onToggle}
              filteredIds={filteredIds}
            />
          ))}
        </div>
      )}
    </div>
  );
}


// ────────────────────────────────────────────────────────────────
// DETAIL PANEL (right / main)
// ────────────────────────────────────────────────────────────────

function DetailPanel({ nodeId, tiers, actorRole }) {
  const { data: node, isLoading } = useQuery({
    queryKey: ['cc-node', nodeId],
    queryFn: () => api(`/api/admin/control-centre/nodes/${encodeURIComponent(nodeId)}`),
    enabled: Boolean(nodeId),
  });

  if (isLoading) {
    return (
      <section className="rounded-lg border border-slate-200 bg-white p-8 text-center text-slate-400">
        <Loader2 className="h-5 w-5 mx-auto animate-spin" />
      </section>
    );
  }
  if (!node) {
    return (
      <section className="rounded-lg border border-slate-200 bg-white p-8 text-center text-slate-500">
        Select a node from the tree.
      </section>
    );
  }

  const Icon = KIND_ICON[node.kind || node.type] || KIND_ICON.default;

  return (
    <section className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid="cc-detail-panel">
      <div className="border-b border-slate-200 p-4">
        <Breadcrumb crumbs={node.breadcrumb || []} />
        <div className="flex items-center gap-2 mt-1">
          <Icon className="h-5 w-5 text-violet-600" />
          <h2 className="text-lg font-bold text-slate-800" data-testid="cc-detail-name">{node.name}</h2>
          {node.slug && <span className="text-[11px] text-slate-500 font-mono">/{node.slug}</span>}
          <span className="text-[10px] uppercase tracking-widest text-slate-400 ml-2">{node.type}</span>
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="border-b border-slate-200 rounded-none w-full justify-start bg-slate-50 h-auto p-0">
          <TabsTrigger value="overview" data-testid="cc-tab-overview" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-violet-600 py-2">Overview</TabsTrigger>
          <TabsTrigger value="people" data-testid="cc-tab-people" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-violet-600 py-2">People</TabsTrigger>
          <TabsTrigger value="ladder" data-testid="cc-tab-ladder" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-violet-600 py-2">Role Ladder</TabsTrigger>
          <TabsTrigger value="vocabulary" data-testid="cc-tab-vocabulary" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-violet-600 py-2">Role Vocabulary</TabsTrigger>
          {actorRole === 'super_admin' && nodeId === 'platform:root' && (
            <TabsTrigger value="billing" data-testid="cc-tab-billing" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-violet-600 py-2">Billing</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="overview" className="p-4 space-y-3" data-testid="cc-overview">
          <OverviewCard node={node} />
        </TabsContent>
        <TabsContent value="people" className="p-4" data-testid="cc-people">
          <PeopleTab nodeId={nodeId} actorRole={actorRole} />
        </TabsContent>
        <TabsContent value="ladder" className="p-4 space-y-4" data-testid="cc-ladder">
          <LadderTab tiers={tiers} actorRole={actorRole} />
          {actorRole === 'super_admin' && <ViewAsLauncher />}
        </TabsContent>
        <TabsContent value="vocabulary" className="p-4" data-testid="cc-vocabulary">
          <VocabularyTab />
        </TabsContent>
        {actorRole === 'super_admin' && nodeId === 'platform:root' && (
          <TabsContent value="billing" className="p-4" data-testid="cc-billing">
            <BillingTab />
          </TabsContent>
        )}
      </Tabs>
    </section>
  );
}


function Breadcrumb({ crumbs }) {
  if (!crumbs.length) return null;
  return (
    <nav className="text-[11px] text-slate-500 flex items-center gap-1 flex-wrap" data-testid="cc-breadcrumb">
      {crumbs.map((c, i) => (
        <React.Fragment key={c.id}>
          {i > 0 && <ChevronRight className="h-3 w-3" />}
          <span className={i === crumbs.length - 1 ? 'text-slate-700 font-medium' : ''}>{c.name}</span>
        </React.Fragment>
      ))}
    </nav>
  );
}


function OverviewCard({ node }) {
  const copyId = () => {
    navigator.clipboard.writeText(node.id)
      .then(() => toast.success('Node id copied'))
      .catch(() => toast.error('Copy failed'));
  };
  return (
    <div className="space-y-3">
      <NodeStatsRow nodeId={node.id} />
      <div className="grid grid-cols-2 gap-3 text-sm">
        <Meta label="Node id"><button className="font-mono text-[11px] flex items-center gap-1 hover:text-violet-600" onClick={copyId}><Copy className="h-3 w-3" />{node.id}</button></Meta>
        <Meta label="Type" value={node.type} />
        {node.kind && <Meta label="Kind" value={node.kind} />}
        {node.slug && <Meta label="Slug" value={node.slug} />}
        {node.meta?.level && <Meta label="Level" value={node.meta.level} />}
        {node.meta?.country && <Meta label="Country" value={node.meta.country} />}
        {node.meta?.status && <Meta label="Status" value={node.meta.status} />}
      </div>
      {node.meta?.description && (
        <div className="rounded border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
          {node.meta.description}
        </div>
      )}
    </div>
  );
}


/**
 * Live counts for the selected node — members, active admins, revenue.
 * Backend: GET /nodes/{id}/stats. Cached client-side for 60s so bouncing
 * around the tree stays snappy.
 */
function NodeStatsRow({ nodeId }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['cc-node-stats', nodeId],
    queryFn: () => api(`/api/admin/control-centre/nodes/${encodeURIComponent(nodeId)}/stats`),
    enabled: Boolean(nodeId),
    staleTime: 60_000,
  });

  if (error) return null;

  const s = data || {};
  const fmt = (n) => (typeof n === 'number' ? n.toLocaleString() : '—');
  const money = (amt, cur) =>
    (Number.isFinite(amt) && amt > 0)
      ? `${cur || 'USD'} ${amt.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
      : `${cur || 'USD'} 0`;

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="cc-node-stats">
      <StatTile
        icon={Users}
        label={s.members?.label || 'Members'}
        value={isLoading ? '…' : fmt(s.members?.count)}
        tone="emerald"
        testId="stat-members"
      />
      <StatTile
        icon={User}
        label={`Active admins · ${s.active_users?.window_days || 30}d`}
        value={isLoading ? '…' : fmt(s.active_users?.count)}
        tone="violet"
        testId="stat-active-users"
      />
      <StatTile
        icon={TrendingUp}
        label={`Revenue · ${s.revenue?.window_days || 30}d`}
        value={isLoading ? '…' : money(s.revenue?.amount, s.revenue?.currency)}
        tone="sky"
        testId="stat-revenue"
      />
    </div>
  );
}


function StatTile({ icon: Icon, label, value, tone = 'slate', testId }) {
  const toneMap = {
    emerald: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    violet:  'bg-violet-50 text-violet-800 border-violet-200',
    sky:     'bg-sky-50 text-sky-800 border-sky-200',
    slate:   'bg-slate-50 text-slate-800 border-slate-200',
  };
  return (
    <div className={`rounded-md border p-3 ${toneMap[tone] || toneMap.slate}`} data-testid={testId}>
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider opacity-70">
        <Icon className="h-3 w-3" />
        {label}
      </div>
      <div className="text-lg font-semibold mt-0.5">{value}</div>
    </div>
  );
}


function Meta({ label, value, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-400">{label}</div>
      <div className="text-sm text-slate-800">{children || value || '—'}</div>
    </div>
  );
}


function PeopleTab({ nodeId, actorRole }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['cc-people', nodeId],
    queryFn: () => api(`/api/admin/control-centre/people/${encodeURIComponent(nodeId)}`),
    enabled: Boolean(nodeId),
  });

  const [showGrant, setShowGrant] = useState(false);
  const [grantEmail, setGrantEmail] = useState('');
  const [grantRole, setGrantRole] = useState('viewer');

  const grant = useMutation({
    mutationFn: async () => {
      // Find admin by email first (admin lookup is via /admin/rbac/users)
      const users = await api(`/api/admin/rbac/users?search=${encodeURIComponent(grantEmail)}`);
      const match = (users?.users || users || []).find((u) => u.email === grantEmail);
      if (!match) throw new Error(`No admin with email "${grantEmail}"`);
      return api('/api/admin/control-centre/grants', {
        method: 'POST',
        body: JSON.stringify({
          admin_id: match._id || match.id,
          node_id: nodeId,
          role: grantRole,
        }),
      });
    },
    onSuccess: () => {
      toast.success('Access granted');
      setShowGrant(false); setGrantEmail(''); setGrantRole('viewer');
      qc.invalidateQueries({ queryKey: ['cc-people', nodeId] });
    },
    onError: (e) => toast.error(e.message || 'Grant failed'),
  });

  const revoke = useMutation({
    mutationFn: (grantId) => api(`/api/admin/control-centre/grants/${grantId}/revoke`, {
      method: 'POST', body: JSON.stringify({}),
    }),
    onSuccess: () => {
      toast.success('Access revoked');
      qc.invalidateQueries({ queryKey: ['cc-people', nodeId] });
    },
    onError: (e) => toast.error(e.message || 'Revoke failed'),
  });

  const impersonate = useMutation({
    mutationFn: (adminId) => api('/api/admin/control-centre/impersonate', {
      method: 'POST', body: JSON.stringify({ admin_id: adminId }),
    }),
    onSuccess: (r) => {
      // Persist the impersonation token and reload — the app-shell picks
      // it up and shows the existing "Impersonating" banner.
      try {
        // Save current super-admin token so the banner can offer "return to self"
        const currentToken = localStorage.getItem('access_token')
          || localStorage.getItem('token');
        if (currentToken) localStorage.setItem('impersonator_token', currentToken);
        localStorage.setItem('access_token', r.token);
        localStorage.setItem('token', r.token);
      } catch { /* private mode */ }
      toast.success(`Signed in as ${r.user?.email}`);
      // Redirect to the admin home so the shell re-reads the new token
      setTimeout(() => { window.location.href = '/admin'; }, 400);
    },
    onError: (e) => toast.error(e.message || 'Impersonation failed'),
  });

  if (isLoading) return <div className="text-center text-slate-400 py-6"><Loader2 className="h-4 w-4 animate-spin mx-auto" /></div>;
  const people = data?.people || [];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-600">
          {data?.total || 0} people with access
          {Object.entries(data?.by_tier || {}).length > 0 && (
            <span className="ml-2 text-[11px] text-slate-400">
              ({Object.entries(data.by_tier).map(([t, n]) => `${n} ${t}`).join(', ')})
            </span>
          )}
        </div>
        {actorRole !== 'viewer' && (
          <Button
            size="sm"
            data-testid="cc-grant-open"
            onClick={() => setShowGrant((x) => !x)}
          >
            {showGrant ? <X className="h-3.5 w-3.5 mr-1" /> : <User className="h-3.5 w-3.5 mr-1" />}
            {showGrant ? 'Cancel' : 'Grant access'}
          </Button>
        )}
      </div>

      {showGrant && (
        <div className="rounded border border-violet-200 bg-violet-50 p-3 space-y-2" data-testid="cc-grant-form">
          <Input
            data-testid="cc-grant-email"
            placeholder="Admin email"
            value={grantEmail}
            onChange={(e) => setGrantEmail(e.target.value)}
            className="h-8 text-sm"
          />
          <select
            data-testid="cc-grant-role"
            value={grantRole}
            onChange={(e) => setGrantRole(e.target.value)}
            className="w-full rounded border border-slate-300 bg-white h-8 text-sm px-2"
          >
            <option value="viewer">Viewer</option>
            <option value="operator">Operator</option>
            <option value="manager">Manager</option>
            <option value="org_admin">Org Admin</option>
            {(actorRole === 'super_admin' || actorRole === 'platform_admin') && (
              <option value="platform_admin">Platform Admin</option>
            )}
            {actorRole === 'super_admin' && (
              <option value="super_admin">Super Admin</option>
            )}
          </select>
          <Button
            size="sm"
            data-testid="cc-grant-submit"
            onClick={() => grant.mutate()}
            disabled={grant.isPending || !grantEmail.trim()}
            className="w-full"
          >
            {grant.isPending && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}
            Grant
          </Button>
        </div>
      )}

      {people.length === 0 ? (
        <div className="text-center text-slate-400 text-sm py-8 border border-dashed border-slate-200 rounded">
          No one has direct access at this node yet.
        </div>
      ) : (
        <div className="space-y-1" data-testid="cc-people-list">
          {people.map((p) => (
            <div
              key={`${p.id}-${p.source}-${p.grant_id || ''}`}
              className="flex items-center gap-2 rounded border border-slate-200 bg-white p-2"
              data-testid={`cc-person-${p.id}`}
            >
              <User className="h-3.5 w-3.5 text-slate-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-slate-800 truncate">{p.name || p.email}</div>
                <div className="text-[10px] text-slate-500 truncate">{p.email}</div>
              </div>
              <TierBadge tier={p.role} />
              {p.cascade && (
                <span className="text-[9px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">↓ inherited</span>
              )}
              {actorRole === 'super_admin' && (
                <button
                  type="button"
                  title="Sign in as this user"
                  onClick={() => {
                    if (window.confirm(`Sign in as ${p.email}? An audit record will be kept.`)) {
                      impersonate.mutate(p.id);
                    }
                  }}
                  data-testid={`cc-person-impersonate-${p.id}`}
                  className="text-slate-400 hover:text-violet-600 shrink-0"
                  aria-label="Impersonate"
                  disabled={impersonate.isPending}
                >
                  <LogIn className="h-3.5 w-3.5" />
                </button>
              )}
              {p.grant_id && actorRole !== 'viewer' && (
                <button
                  type="button"
                  onClick={() => revoke.mutate(p.grant_id)}
                  className="text-slate-400 hover:text-rose-600 shrink-0"
                  data-testid={`cc-person-revoke-${p.id}`}
                  aria-label="Revoke"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function LadderTab({ tiers, actorRole }) {
  return (
    <div className="space-y-2" data-testid="cc-ladder-tab">
      {tiers.map((t) => (
        <div
          key={t.key}
          className={`rounded border p-3 ${
            t.key === actorRole ? TIER_COLOR[t.key] : 'border-slate-200 bg-white'
          }`}
        >
          <div className="flex items-center gap-2 mb-1">
            {t.key === 'super_admin' && <Crown className="h-4 w-4 text-rose-600" />}
            <span className="font-semibold text-slate-800">{t.label}</span>
            {t.key === actorRole && (
              <span className="text-[10px] uppercase tracking-widest text-emerald-700 ml-auto">You</span>
            )}
          </div>
          <p className="text-xs text-slate-600">{t.blurb}</p>
        </div>
      ))}
    </div>
  );
}


function VocabularyTab() {
  const { data, isLoading } = useQuery({
    queryKey: ['cc-vocabulary'],
    queryFn: () => api('/api/admin/control-centre/role-vocabulary'),
  });
  if (isLoading) return <Loader2 className="h-4 w-4 animate-spin mx-auto" />;
  const mappings = data?.mappings || {};
  return (
    <div className="space-y-3" data-testid="cc-vocabulary-tab">
      <p className="text-xs text-slate-500">
        Each module&apos;s legacy role labels map to the canonical <b>Manager · Operator · Viewer</b> tiers.
        Existing assignments are preserved — only the tier a label collapses into changes.
      </p>
      {Object.entries(mappings).map(([mod, m]) => (
        <div key={mod} className="rounded border border-slate-200 bg-white overflow-hidden">
          <div className="bg-slate-50 border-b border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 capitalize">{mod}</div>
          <table className="w-full text-sm">
            <tbody>
              {Object.entries(m).map(([label, tier]) => (
                <tr key={label} className="border-t border-slate-100">
                  <td className="px-3 py-1.5 text-slate-700">{label}</td>
                  <td className="px-3 py-1.5 text-right"><TierBadge tier={tier} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}


// ────────────────────────────────────────────────────────────────
// BILLING TAB (super-admin only, platform-root only)
// ────────────────────────────────────────────────────────────────

function BillingTab() {
  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ['cc-billing-summary'],
    queryFn: () => api('/api/admin/control-centre/billing/summary'),
  });
  const { data: tenants, isLoading: tenantsLoading } = useQuery({
    queryKey: ['cc-billing-tenants'],
    queryFn: () => api('/api/admin/control-centre/billing/tenants?limit=100'),
  });
  const { data: plans } = useQuery({
    queryKey: ['cc-billing-plans'],
    queryFn: () => api('/api/admin/control-centre/billing/plans'),
  });
  const { data: invoices } = useQuery({
    queryKey: ['cc-billing-invoices'],
    queryFn: () => api('/api/admin/control-centre/billing/invoices?limit=50'),
  });

  return (
    <div className="space-y-4" data-testid="cc-billing-tab">
      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard
          label="Active tenants"
          value={summary?.tenants?.active}
          sub={`${summary?.tenants?.total || 0} total`}
          icon={GraduationCap}
          loading={summaryLoading}
          testid="cc-billing-kpi-tenants"
        />
        <KpiCard
          label="Plans"
          value={summary?.plans?.count}
          icon={CreditCard}
          loading={summaryLoading}
          testid="cc-billing-kpi-plans"
        />
        <KpiCard
          label="Open invoices"
          value={summary?.invoices?.open}
          icon={CreditCard}
          loading={summaryLoading}
          testid="cc-billing-kpi-open-invoices"
        />
        <KpiCard
          label="Paid to date"
          value={summary?.invoices?.total_billed_paid
            ? `$${Number(summary.invoices.total_billed_paid).toLocaleString()}`
            : '$0'}
          icon={CreditCard}
          loading={summaryLoading}
          testid="cc-billing-kpi-paid"
        />
      </div>

      {/* Plans */}
      {(plans?.plans || []).length > 0 && (
        <section>
          <h3 className="text-xs uppercase tracking-widest text-slate-500 mb-2">Plans</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2" data-testid="cc-billing-plans">
            {plans.plans.map((p) => (
              <div key={p.id} className="rounded border border-slate-200 bg-white p-3">
                <div className="font-semibold text-slate-800">{p.name}</div>
                <div className="text-lg text-violet-700">${p.price_monthly || 0}/mo</div>
                <ul className="mt-1 text-[11px] text-slate-500 list-disc pl-4">
                  {(p.features || []).slice(0, 4).map((f, i) => <li key={i}>{f}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Tenants */}
      <section>
        <h3 className="text-xs uppercase tracking-widest text-slate-500 mb-2">
          Tenants {tenants ? `(${tenants.tenants.length})` : ''}
        </h3>
        {tenantsLoading ? (
          <Loader2 className="h-4 w-4 animate-spin mx-auto text-slate-400" />
        ) : (
          <div className="rounded border border-slate-200 bg-white overflow-hidden" data-testid="cc-billing-tenants">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left">Name</th>
                  <th className="px-3 py-2 text-left">Code</th>
                  <th className="px-3 py-2 text-left">Plan</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Created</th>
                </tr>
              </thead>
              <tbody>
                {(tenants?.tenants || []).map((t) => (
                  <tr key={t.id} className="border-t border-slate-100" data-testid={`cc-billing-tenant-${t.id}`}>
                    <td className="px-3 py-2 text-slate-800">{t.name}</td>
                    <td className="px-3 py-2 text-slate-500 font-mono text-[11px]">{t.code || '—'}</td>
                    <td className="px-3 py-2 text-slate-600">
                      {t.plan ? `${t.plan}` : '—'}
                      {t.plan_price ? <span className="text-slate-400 text-[11px] ml-1">${t.plan_price}/mo</span> : null}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                        t.status === 'active' ? 'bg-emerald-100 text-emerald-800'
                        : t.status === 'trial' ? 'bg-sky-100 text-sky-800'
                        : 'bg-slate-100 text-slate-600'
                      }`}>{t.status}</span>
                    </td>
                    <td className="px-3 py-2 text-slate-500 text-[11px]">
                      {t.created_at ? new Date(t.created_at).toLocaleDateString() : '—'}
                    </td>
                  </tr>
                ))}
                {(!tenants || tenants.tenants.length === 0) && (
                  <tr><td colSpan={5} className="text-center py-6 text-slate-400 text-sm">No tenants yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Recent invoices */}
      {(invoices?.invoices || []).length > 0 && (
        <section>
          <h3 className="text-xs uppercase tracking-widest text-slate-500 mb-2">Recent invoices</h3>
          <div className="rounded border border-slate-200 bg-white overflow-hidden" data-testid="cc-billing-invoices">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left">Tenant</th>
                  <th className="px-3 py-2 text-left">Amount</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Period</th>
                </tr>
              </thead>
              <tbody>
                {invoices.invoices.map((i) => (
                  <tr key={i.id} className="border-t border-slate-100">
                    <td className="px-3 py-2 text-slate-800">{i.tenant_name || i.tenant_id}</td>
                    <td className="px-3 py-2 text-slate-700">${i.amount}</td>
                    <td className="px-3 py-2 text-slate-600 capitalize">{i.status}</td>
                    <td className="px-3 py-2 text-slate-500 text-[11px]">{i.period || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}


function KpiCard({ label, value, sub, icon: Icon, loading, testid }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-widest text-slate-500">{label}</span>
        {Icon && <Icon className="h-3.5 w-3.5 text-violet-500" />}
      </div>
      <div className="text-2xl font-bold text-slate-800 mt-1">
        {loading ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : (value ?? '—')}
      </div>
      {sub && <div className="text-[10px] text-slate-400">{sub}</div>}
    </div>
  );
}
